import { Grid, Layout, Menu } from "antd";
import SVGIcon from "../../../../utils/SVGIcon";
import { CONSTANT } from "../../../../config/Constant";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setIsRestricted } from "../../../../features/auth/authSlice";
import {
  SettingOutlined,
  ShopOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  ApiOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";

const SidebarComponent = ({
  collapsed,
  setCollapsed,
  siderWidth,
  setSiderWidth,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { moduleRightsData } = useSelector((state) => state.auth);

  // IsPicklistWorkflowEnabledAndMandatory:
  // false = Picklist workflow is COMPLETELY DISABLED (hide Picklist menu entirely)
  // true  = Picklist workflow is MANDATORY (show Picklist menu)
  const { tenantPackingSettings } = useSelector((state) => state.profile);
  const isPicklistWorkflowEnabled =
    tenantPackingSettings?.isPicklistWorkflowEnabledAndMandatory ?? false;

  const [menuItems, setMenuItems] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  const isAdmin = localStorage.getItem("isa") || "false";

  const isResizingRef = useRef(false);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizingRef.current || collapsed) return;

      if (e.clientX >= 180 && e.clientX <= 400) {
        setSiderWidth(e.clientX);
      }
    };

    const onMouseUp = () => {
      isResizingRef.current = false;
      document.body.classList.remove("resizing");
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [collapsed, setSiderWidth]);

  const items = useMemo(() => {
    // Reports tab is an umbrella over five report modules. `filterMenuByPermission`
    // only supports a single `code` per leaf, so to avoid touching that shared
    // utility we pick the FIRST report module the user has View on and use that
    // as the Reports leaf's `code` — the existing filter then approves the leaf
    // naturally. If the user has none of them, this stays undefined, the filter
    // rejects, and the tab is hidden. Scope of this trick is limited to the
    // Reports leaf below; every other menu item still uses a fixed `code`.
    const REPORTS_TAB_MODULES = [
      CONSTANT.MODULE_CODE.STOCK_REPORT,
      CONSTANT.MODULE_CODE.STOCK_SUMMARY_REPORT,
      CONSTANT.MODULE_CODE.STOCK_VALUATION_REPORT,
      CONSTANT.MODULE_CODE.SALES_REPORT,
      CONSTANT.MODULE_CODE.PURCHASE_REPORT,
      CONSTANT.MODULE_CODE.INVOICE_REPORT,
      CONSTANT.MODULE_CODE.REPORT_EXPORT,
      CONSTANT.MODULE_CODE.COUNTER_SALES_REPORT,
      CONSTANT.MODULE_CODE.GSTR_REPORT,
    ];
    const reportsTabCode = REPORTS_TAB_MODULES.find((c) =>
      moduleRightsData?.[c]?.includes(CONSTANT.MODULE_PERMISSION.VIEW),
    );

    return [
      {
        key: `/${CONSTANT.ADMIN_ROUTE.TENANT}`,
        label: <Link to={`/${CONSTANT.ADMIN_ROUTE.TENANT}`}>Tenant</Link>,
        icon: <SVGIcon icon="menu_building" size={20} />,
        code: CONSTANT.MODULE_CODE.TENANT,
      },
      {
        key: `/${CONSTANT.ADMIN_ROUTE.TENANT_INQUIRY}`,
        label: (
          <Link to={`/${CONSTANT.ADMIN_ROUTE.TENANT_INQUIRY}`}>
            Tenant Inquiry
          </Link>
        ),
        icon: <SVGIcon icon="menu_building" size={20} />,
        code: CONSTANT.MODULE_CODE.TENANT_INQUIRY,
      },
      {
        // Admin integrations group (WhatsApp today; SMS/Payment/etc. later).
        key: "adminIntegrationsMenu",
        label: "Integrations",
        icon: <SVGIcon icon="menu_folder" size={20} />,
        children: [
          {
            // Opens the WhatsApp Monitoring dashboard (the hub); Templates is a link on that page.
            key: `/${CONSTANT.ADMIN_ROUTE.WHATSAPP_MONITORING}`,
            label: (
              <Link to={`/${CONSTANT.ADMIN_ROUTE.WHATSAPP_MONITORING}`}>WhatsApp</Link>
            ),
            icon: <WhatsAppOutlined style={{ fontSize: 18 }} />,
            // Reuses the Tenant super-admin module gate (no new module/re-login).
            code: CONSTANT.MODULE_CODE.TENANT,
          },
        ],
      },
      {
        key: "itemsMenu",
        label: "Items",
        icon: <SVGIcon icon="menu_product" size={20} />,
        children: [
          {
            key: `/${CONSTANT.ROUTE.PRODUCT}`,
            label: <Link to={`/${CONSTANT.ROUTE.PRODUCT}`}>Items</Link>,
            icon: <SVGIcon icon="item" size={20} />,
            code: CONSTANT.MODULE_CODE.PRODUCT,
          },
          {
            key: `/${CONSTANT.ROUTE.PRODUCT_GROUP}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.PRODUCT_GROUP}`}>Item Groups</Link>
            ),
            icon: <SVGIcon icon="item_group" size={20} />,
            code: CONSTANT.MODULE_CODE.PRODUCT_GROUP,
          },
          {
            key: `/${CONSTANT.ROUTE.ITEM_DEFAULT_BIN}`,
            label: (
              <Link
                to={`/${CONSTANT.ROUTE.ITEM_DEFAULT_BIN}`}
                title="Manage default bin per item & warehouse"
              >
                Item Default Bin
              </Link>
            ),
            icon: <SVGIcon icon="item" size={20} />,
            code: CONSTANT.MODULE_CODE.ITEM_DEFAULT_BIN,
          },
          // {
          //   key: `/${CONSTANT.ROUTE.COMPOSITE_ITEMS}`,
          //   label: (
          //     <Link to={`/${CONSTANT.ROUTE.COMPOSITE_ITEMS}`}>
          //       Composite Items
          //     </Link>
          //   ),
          //   icon: <SVGIcon icon="composite_item" size={18} />,
          //   code: CONSTANT.MODULE_CODE.PRODUCT,
          // },
        ],
      },
      {
        key: "purchasesMenu",
        label: "Purchases",
        icon: <SVGIcon icon="menu_cart" size={20} />,
        children: [
          {
            key: `/${CONSTANT.ROUTE.VENDOR}`,
            label: <Link to={`/${CONSTANT.ROUTE.VENDOR}`}>Vendors</Link>,
            icon: <SVGIcon icon="menu_building" size={20} />,
            code: CONSTANT.MODULE_CODE.VENDOR,
          },
          {
            key: `/${CONSTANT.ROUTE.PURCHASE_ORDER}`, // Use full path with leading slash
            label: (
              <Link to={`/${CONSTANT.ROUTE.PURCHASE_ORDER}`}>
                Purchase Orders
              </Link>
            ),
            icon: <SVGIcon icon="menu_purchase_order" size={19} />,
            code: CONSTANT.MODULE_CODE.PURCHASE_ORDER,
          },
          {
            key: `/${CONSTANT.ROUTE.PURCHASE_RECEIVE}`, // Use full path with leading slash
            label: (
              <Link
                to={`/${CONSTANT.ROUTE.PURCHASE_RECEIVE}`}
                title="Purchase Receives"
              >
                Purchase Receives
              </Link>
            ),
            icon: <SVGIcon icon="menu_purchase_receive" size={19} />,
            code: CONSTANT.MODULE_CODE.PURCHASE_RECEIVE,
          },
        ],
      },
      {
        key: "salesMenu",
        label: "Sales",
        icon: <ShopOutlined style={{ fontSize: "20px" }} />,
        children: [
          {
            key: `/${CONSTANT.ROUTE.CUSTOMER}`,
            label: <Link to={`/${CONSTANT.ROUTE.CUSTOMER}`}>Customers</Link>,
            icon: <SVGIcon icon="menu_customers" size={20} />,
            code: CONSTANT.MODULE_CODE.CUSTOMER,
          },
          {
            key: `/${CONSTANT.ROUTE.QUOTATION}`,
            label: <Link to={`/${CONSTANT.ROUTE.QUOTATION}`}>Quotations</Link>,
            icon: <FileTextOutlined style={{ fontSize: "18px" }} />,
            code: CONSTANT.MODULE_CODE.QUOTATION,
          },
          {
            key: `/${CONSTANT.ROUTE.SALES_ORDER}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.SALES_ORDER}`}>Sales Orders</Link>
            ),
            icon: <FileTextOutlined style={{ fontSize: "18px" }} />,
            code: CONSTANT.MODULE_CODE.SALES_ORDER,
          },
          // Picklist menu: Only visible when isPicklistWorkflowEnabledAndMandatory is true
          // IsPicklistWorkflowEnabledAndMandatory: false = hide entirely, true = show
          isPicklistWorkflowEnabled && {
            key: `/${CONSTANT.ROUTE.PICKLIST}`,
            label: <Link to={`/${CONSTANT.ROUTE.PICKLIST}`}>Picklists</Link>,
            icon: <SVGIcon icon="picklist" size={22} />,
            code: CONSTANT.MODULE_CODE.PICKLIST,
          },
          {
            key: `/${CONSTANT.ROUTE.PACKAGES}`,
            label: <Link to={`/${CONSTANT.ROUTE.PACKAGES}`}>Packages</Link>,
            icon: <SVGIcon icon="menu_product" size={20} />,
            code: CONSTANT.MODULE_CODE.PACKAGE,
          },
          {
            key: `/${CONSTANT.ROUTE.DELIVERY_CHALLAN}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.DELIVERY_CHALLAN}`}>
                Delivery Challans
              </Link>
            ),
            icon: <SVGIcon icon="menu_shipping" size={19} />,
            code: CONSTANT.MODULE_CODE.DELIVERY_CHALLAN,
          },
          {
            key: `/invoices`,
            label: <Link to={`/invoices`}>Invoices</Link>,
            icon: <SVGIcon icon="menu_invoice" size={20} />,
            code: CONSTANT.MODULE_CODE.INVOICE,
          },
          {
            key: `/${CONSTANT.ROUTE.SHIPMENT}`,
            label: <Link to={`/${CONSTANT.ROUTE.SHIPMENT}`}>Shipments</Link>,
            icon: <SVGIcon icon="menu_delivery_truck" size={19} />,
            code: CONSTANT.MODULE_CODE.SHIPMENT,
          },
          {
            key: `/${CONSTANT.ROUTE.SALES_RETURN}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.SALES_RETURN}`}>Sales Returns</Link>
            ),
            icon: <SVGIcon icon="menu_sales_return" size={18} />,
            code: CONSTANT.MODULE_CODE.SALES_RETURN,
          },
          {
            key: `/${CONSTANT.ROUTE.BACKORDER}`,
            label: <Link to={`/${CONSTANT.ROUTE.BACKORDER}`}>Backorders</Link>,
            icon: <SVGIcon icon="menu_back_order" size={22} />,
            code: CONSTANT.MODULE_CODE.BACKORDER,
          },
          {
            key: `/${CONSTANT.ROUTE.CREDIT_NOTE}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.CREDIT_NOTE}`}>Credit Notes</Link>
            ),
            icon: <SVGIcon icon="menu_credit_notes" size={20} />,
            // TODO: Change to SALES_CREDIT_NOTE when module is added to backend
            code: CONSTANT.MODULE_CODE.SALES_CREDIT_NOTE,
          },
          {
            key: `/${CONSTANT.ROUTE.COUNTER_SALE}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.COUNTER_SALE}`}>Counter Sales</Link>
            ),
            icon: <SVGIcon icon="menu_invoice" size={20} />,
            code: CONSTANT.MODULE_CODE.COUNTER_SALES,
          },
        ].filter(Boolean),
      },
      {
        key: "inventoryMenu",
        label: "Inventory",
        icon: <DatabaseOutlined style={{ fontSize: "20px" }} />,
        children: [
          {
            key: `/${CONSTANT.ROUTE.ITEM_ADJUSTMENT}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.ITEM_ADJUSTMENT}`}>Adjustments</Link>
            ),
            icon: <FileTextOutlined style={{ fontSize: "18px" }} />,
            code: CONSTANT.MODULE_CODE.ITEM_ADJUSTMENT,
          },
          {
            key: `/${CONSTANT.ROUTE.TRANSFER_ORDER}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.TRANSFER_ORDER}`}>
                Transfer Orders
              </Link>
            ),
            icon: <SVGIcon icon="transfer_order" size={20} />,
            code: CONSTANT.MODULE_CODE.TRANSFER_ORDER,
          },
          {
            key: `/${CONSTANT.ROUTE.STOCK_LEDGER}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.STOCK_LEDGER}`}>Stock Ledger</Link>
            ),
            icon: <FileTextOutlined style={{ fontSize: "18px" }} />,
            code: CONSTANT.MODULE_CODE.STOCK_REPORT,
          },
        ],
      },
      {
        key: `/${CONSTANT.ROUTE.REPORTS}`,
        label: <Link to={`/${CONSTANT.ROUTE.REPORTS}`}>Reports</Link>,
        icon: <BarChartOutlined style={{ fontSize: "20px" }} />,
        // Visible when the user has View on ANY of the five report modules —
        // see the REPORTS_TAB_MODULES / reportsTabCode computation at the top
        // of this useMemo.
        code: reportsTabCode,
      },
      {
        key: "masterMenu",
        label: "Master",
        icon: <SVGIcon icon="menu_folder" size={22} />,
        children: [
          {
            key: `/${CONSTANT.ROUTE.BRAND}`, // Use full path with leading slash
            label: <Link to={`/${CONSTANT.ROUTE.BRAND}`}>Brands</Link>,
            icon: <SVGIcon icon="menu_product" size={18} />,
            code: CONSTANT.MODULE_CODE.BRAND,
          },
          {
            key: `/${CONSTANT.ROUTE.TRANSPORTER}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.TRANSPORTER}`}>Transporters</Link>
            ),
            icon: <FileTextOutlined style={{ fontSize: "18px" }} />,
            code: CONSTANT.MODULE_CODE.TRANSPORTER,
          },
          {
            key: `/${CONSTANT.ROUTE.UNIT_OF_MEASUREMENT}`,
            label: (
              <Link
                to={`/${CONSTANT.ROUTE.UNIT_OF_MEASUREMENT}`}
                title="Units of Measurement"
              >
                Units of Measurement
              </Link>
            ),
            icon: <SVGIcon icon="menu_measurement" size={18} />,
            code: CONSTANT.MODULE_CODE.UNIT_OF_MEASUREMENT,
          },
          {
            key: `/${CONSTANT.ROUTE.UQC_MASTER}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.UQC_MASTER}`} title="UQC Master">
                UQC Master
              </Link>
            ),
            icon: <SVGIcon icon="menu_measurement" size={18} />,
            code: CONSTANT.MODULE_CODE.UQC_MASTER,
          },

          // {
          //   key: `/${CONSTANT.ROUTE.WAREHOUSE}`,
          //   label: "Warehouse",
          //   code: CONSTANT.MODULE_CODE.WAREHOUSE,
          // },
          {
            key: `/${CONSTANT.ROUTE.LOCATION}`,
            label: <Link to={`/${CONSTANT.ROUTE.LOCATION}`}>Locations</Link>,
            icon: <SVGIcon icon="menu_location" size={18} />,
            code: CONSTANT.MODULE_CODE.LOCATION,
          },
          {
            key: `/${CONSTANT.ROUTE.ITEM_CATEGORY}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.ITEM_CATEGORY}`}>
                Discount Categories
              </Link>
            ),
            icon: <SVGIcon icon="item_category" size={18} />,
            code: CONSTANT.MODULE_CODE.CATEGORY,
          },
          {
            key: `/${CONSTANT.ROUTE.ITEM_SEGMENT}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.ITEM_SEGMENT}`}>Item Segments</Link>
            ),
            icon: <SVGIcon icon="item_category" size={18} />,
            code: CONSTANT.MODULE_CODE.ITEM_SEGMENT,
          },
          {
            key: `/${CONSTANT.ROUTE.PRODUCT_GROUP_MASTER}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.PRODUCT_GROUP_MASTER}`}>
                Product Groups
              </Link>
            ),
            icon: <SVGIcon icon="item_category" size={18} />,
            code: CONSTANT.MODULE_CODE.PRODUCT_GROUP_MASTER,
          },
          {
            key: `/${CONSTANT.ROUTE.CUSTOMER_CATEGORY}`,
            label: (
              <Link
                to={`/${CONSTANT.ROUTE.CUSTOMER_CATEGORY}`}
                title="Customer Discount Categories"
              >
                Customer Discount Categories
              </Link>
            ),
            icon: <SVGIcon icon="customer_category" size={18} />,
            code: CONSTANT.MODULE_CODE.CATEGORY,
          },
          {
            key: `/${CONSTANT.ROUTE.CUSTOMER_ACCOUNT_TYPE}`,
            label: (
              <Link
                to={`/${CONSTANT.ROUTE.CUSTOMER_ACCOUNT_TYPE}`}
                title="Customer Account Types"
              >
                Customer Account Types
              </Link>
            ),
            icon: <SVGIcon icon="customer_category" size={18} />,
            code: CONSTANT.MODULE_CODE.CUSTOMER_ACCOUNT_TYPE,
          },
          {
            key: `/${CONSTANT.ROUTE.CURRENCY}`,
            label: <Link to={`/${CONSTANT.ROUTE.CURRENCY}`}>Currencies</Link>,
            icon: <SVGIcon icon="menu_dollar" size={20} />,
            code: CONSTANT.MODULE_CODE.CURRENCY,
          },
          {
            key: `/${CONSTANT.ROUTE.APPROVER}`,
            label: <Link to={`/${CONSTANT.ROUTE.APPROVER}`}>Approvers</Link>,
            icon: <SVGIcon icon="approvers" size={18} />,
            code: CONSTANT.MODULE_CODE.APPROVER,
          },
          {
            key: `/${CONSTANT.ROUTE.TERRITORY}`,
            label: <Link to={`/${CONSTANT.ROUTE.TERRITORY}`}>Territories</Link>,
            icon: <SVGIcon icon="territory" size={18} />,
            code: CONSTANT.MODULE_CODE.TERRITORY,
          },
          {
            key: `/${CONSTANT.ROUTE.MANUFACTURER}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.MANUFACTURER}`}>Manufacturer</Link>
            ),
            icon: <SVGIcon icon="manufacturer" size={20} />,
            code: CONSTANT.MODULE_CODE.MANUFACTURER,
          },
          {
            key: `/${CONSTANT.ROUTE.DISCOUNT_SCHEME}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.DISCOUNT_SCHEME}`}>
                Discount Schemes
              </Link>
            ),
            icon: <SVGIcon icon="menu_dollar" size={20} />,
            code: CONSTANT.MODULE_CODE.DISCOUNT_SCHEME,
          },
          {
            key: `/${CONSTANT.ADMIN_ROUTE.EMAIL_PROVIDER}`, // Use full path with leading slash
            label: (
              <Link to={`/${CONSTANT.ADMIN_ROUTE.EMAIL_PROVIDER}`}>
                Email Providers
              </Link>
            ),
            icon: <SVGIcon icon="menu_email_provider" size={20} />,
            code: CONSTANT.MODULE_CODE.EMAIL_PROVIDER,
          },
          {
            key: `/${CONSTANT.ADMIN_ROUTE.EMAIL_TEMPLATE}`, // Use full path with leading slash
            label: (
              <Link to={`/${CONSTANT.ADMIN_ROUTE.EMAIL_TEMPLATE}`}>
                Email Templates
              </Link>
            ),
            icon: <SVGIcon icon="email_template" size={20} />,
            code: CONSTANT.MODULE_CODE.EMAIL_TEMPLATE,
          },
        ],
      },
      {
        key: "SettingsMenu",
        label: "Settings",
        icon: <SettingOutlined style={{ fontSize: "20px" }} />,
        // code: CONSTANT.MODULE_CODE.ORG_SETTINGS,
        children: [
          {
            key: `/${CONSTANT.ROUTE.USER}`, // Use full path with leading slash
            label: <Link to={`/${CONSTANT.ROUTE.USER}`}>Users</Link>,
            icon: <SVGIcon icon="menu_user" size={18} />,
            code: CONSTANT.MODULE_CODE.USER,
          },
          {
            key: `/${CONSTANT.ROUTE.ROLE}`, // Use full path with leading slash
            label: <Link to={`/${CONSTANT.ROUTE.ROLE}`}>Roles</Link>,
            icon: <SVGIcon icon="menu_user_role" size={18} />,
            code: CONSTANT.MODULE_CODE.ROLE,
          },
          {
            key: CONSTANT.ROUTE.SETTINGS,
            label: (
              <Link to={CONSTANT.ROUTE.SETTINGS}>Organization Setting</Link>
            ),
            icon: <SettingOutlined style={{ fontSize: "18px" }} />,
            code: CONSTANT.MODULE_CODE.ORG_SETTINGS,
          },
          isAdmin === "false" && {
            key: CONSTANT.ROUTE.TENANT_EMAIL_CONFIGURATION,
            label: (
              <Link to={CONSTANT.ROUTE.TENANT_EMAIL_CONFIGURATION}>
                Email Configuration
              </Link>
            ),
            icon: <SVGIcon icon="mail_setting" size={20} />,
            code: CONSTANT.MODULE_CODE.TENANT_EMAIL_CONFIGURATION,
          },
          {
            key: CONSTANT.ROUTE.TENANT_EMAIL_TEMPLATE,
            label: (
              <Link to={CONSTANT.ROUTE.TENANT_EMAIL_TEMPLATE}>
                Email Template
              </Link>
            ),
            icon: <SVGIcon icon="email_template" size={22} />,
            code: CONSTANT.MODULE_CODE.TENANT_EMAIL_TEMPLATE,
          },
          {
            key: `/${CONSTANT.ROUTE.FISCAL_CALENDAR}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.FISCAL_CALENDAR}`}>
                Fiscal Calendar
              </Link>
            ),
            icon: <SVGIcon icon="menu_calender" size={20} />,
            code: CONSTANT.MODULE_CODE.FISCAL_CALENDAR,
          },
          {
            key: `/${CONSTANT.ROUTE.IMPORT_TEMPLATE}`,
            label: (
              <Link to={`/${CONSTANT.ROUTE.IMPORT_TEMPLATE}`}>
                Import Templates
              </Link>
            ),
            icon: <SVGIcon icon="menu_folder" size={20} />,
            code: CONSTANT.MODULE_CODE.IMPORT_TEMPLATE,
          },
        ].filter(Boolean),
      },
      isAdmin === "false" && {
        key: CONSTANT.ROUTE.INTEGRATIONS,
        label: <Link to={CONSTANT.ROUTE.INTEGRATIONS}>Integrations</Link>,
        icon: <ApiOutlined style={{ fontSize: "20px" }} />,
        code: CONSTANT.MODULE_CODE.WHATSAPP_CONFIGURATION,
      },
    ].filter(Boolean);
  }, [isPicklistWorkflowEnabled, moduleRightsData]);

  const filterMenuByPermission = (menuItems, permissions) => {
    const filtered = menuItems
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterMenuByPermission(
            item.children,
            permissions,
          );
          return filteredChildren.length > 0
            ? { ...item, children: filteredChildren }
            : null;
        }
        const hasViewPermission =
          permissions?.hasOwnProperty(item.code) &&
          permissions[item.code]?.includes(CONSTANT.MODULE_PERMISSION.VIEW);
        return hasViewPermission ? item : null;
      })
      .filter(Boolean);
    return filtered;
  };

  const updateMenuSelection = () => {
    const currentPath = location.pathname;

    let newSelectedKeys = [];
    let newOpenKeys = [];

    const findMatch = (menuArray) => {
      menuArray.forEach((item) => {
        if (item.children) {
          item.children.forEach((child) => {
            if (
              currentPath === child.key ||
              currentPath.startsWith(child.key + "/")
            ) {
              newSelectedKeys = [child.key];
              newOpenKeys = [item.key]; // Set parent key when child is matched
            }
          });
        } else if (
          currentPath === item.key ||
          currentPath.startsWith(item.key + "/")
        ) {
          newSelectedKeys = [item.key];
        }
      });
    };

    findMatch(items);

    // Fallback to current path if no match found
    if (newSelectedKeys.length === 0) {
      newSelectedKeys = [currentPath];
    }

    setSelectedKeys(newSelectedKeys);
    setOpenKeys(newOpenKeys);
  };

  const handleMenuClick = ({ key }) => {
    dispatch(setIsRestricted(false));
    navigate(key);
    setSelectedKeys([key]);

    if (isMobile) {
      setCollapsed(true);
    }

    // Find and open parent menu if applicable
    const parentItem = items.find((item) =>
      item.children?.some((child) => child.key === key),
    );
    setOpenKeys(parentItem ? [parentItem.key] : []);
  };

  useEffect(() => {
    if (moduleRightsData && Object.keys(moduleRightsData).length > 0) {
      const filtered = filterMenuByPermission(items, moduleRightsData);
      setMenuItems(filtered);
      updateMenuSelection(); // Update selection after filtering
    }
    // else {
    //   // Show all menu items if permissions are not loaded yet
    //   // This prevents the sidebar from being empty
    //   setMenuItems(items);
    //   updateMenuSelection();
    // }
  }, [moduleRightsData, items]);

  useEffect(() => {
    updateMenuSelection();
  }, [location.pathname]);

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  return (
    <Layout.Sider
      collapsed={collapsed}
      // width={250}
      width={collapsed ? 0 : siderWidth}
      collapsedWidth={0}
      className="main__page__appsidebar"
      style={{ position: "relative" }}
    >
      {/* <div className="sidebarLogoSec">       
        <Link to={CONSTANT.DEFAULT_ROUTE}>
          <img src={SiteLogo} className="logo" alt="Site Logo" />
        </Link>
        <span style={{ zIndex: 9 }} onClick={() => setCollapsed(!collapsed)}>         
          <SVGIcon icon="toggle_menu" />
        </span>
      </div> */}
      {!collapsed && (
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={isMobile ? undefined : openKeys}
          onOpenChange={handleOpenChange}
          expandIcon={<SVGIcon icon="down_arrow" />}
          onClick={handleMenuClick}
          items={menuItems}
        />
      )}
      {!collapsed && (
        <div
          className="custom-splitter"
          onMouseDown={() => {
            isResizingRef.current = true;
            document.body.classList.add("resizing");
          }}
        />
      )}
    </Layout.Sider>
  );
};

export default SidebarComponent;
